import { supabase } from "@/integrations/supabase/client";
import type { Profile } from "@/hooks/useProfile";

export interface ProfileUpdate {
  username?: string;
  display_name?: string;
  bio?: string;
  profession?: string;
  location?: string;
  skills?: string[];
  languages?: string[];
  years_experience?: number;
  education?: string;
  portfolio_url?: string;
  instagram_username?: string;
  linkedin_username?: string;
  avatar_url?: string;
  portfolio_items?: any[];
  experience_items?: any[];
  hire_me_enabled?: boolean;
}

export interface ProfileFilters {
  profession?: string;
  location?: string;
  skills?: string[];
  featured?: boolean;
}

export interface PaginationOptions {
  page?: number;
  pageSize?: number;
  cursor?: string;
}

export const profilesService = {
  // Get single profile by ID or username
  async getProfile(idOrSlug: string): Promise<Profile> {
    const isUuid = idOrSlug.match(
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    );

    const query = supabase.from("profiles").select(`*`);

    const { data, error } = isUuid
      ? await query.eq("id", idOrSlug).single()
      : await query.eq("username_slug", idOrSlug).single();

    if (error) throw error;
    return data;
  },

  // Get multiple profiles with filters and pagination
  async getProfiles(
    filters: ProfileFilters = {},
    pagination: PaginationOptions = {}
  ): Promise<{ profiles: Profile[]; hasMore: boolean; nextCursor?: string }> {
    const { page = 1, pageSize = 20 } = pagination;
    const offset = (page - 1) * pageSize;

    let query = supabase
      .from("profiles")
      .select(
        `
        *,
        portfolio_items(count),
        experience_items(count)
      `
      )
      .range(offset, offset + pageSize);

    // Apply filters
    if (filters.profession) {
      query = query.ilike("profession", `%${filters.profession}%`);
    }
    if (filters.location) {
      query = query.ilike("location", `%${filters.location}%`);
    }
    if (filters.skills?.length) {
      query = query.overlaps("skills", filters.skills);
    }
    if (filters.featured !== undefined) {
      query = query.eq("is_featured", filters.featured);
    }

    const { data, error } = await query.order("created_at", {
      ascending: false,
    });

    if (error) throw error;

    return {
      profiles: data || [],
      hasMore: data?.length === pageSize + 1,
      nextCursor: data?.length === pageSize + 1 ? data[pageSize].id : undefined,
    };
  },

  // Update profile
  async updateProfile(id: string, updates: ProfileUpdate): Promise<Profile> {
    const { data, error } = await supabase
      .from("profiles")
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Delete profile
  async deleteProfile(id: string): Promise<void> {
    const { error } = await supabase.from("profiles").delete().eq("id", id);

    if (error) throw error;
  },

  // Increment view count
  async incrementViewCount(profileId: string): Promise<void> {
    const { error } = await supabase.rpc("increment_profile_views", {
      profile_id: profileId,
    });

    if (error) throw error;
  },

  // Like/unlike profile
  async toggleLike(
    profileId: string,
    userId: string,
    isLiked: boolean
  ): Promise<void> {
    if (isLiked) {
      const { error } = await supabase
        .from("profile_likes")
        .insert({ profile_id: profileId, user_id: userId });
      if (error) throw error;
    } else {
      const { error } = await supabase
        .from("profile_likes")
        .delete()
        .eq("profile_id", profileId)
        .eq("user_id", userId);
      if (error) throw error;
    }
  },

  // Get like status
  async getLikeStatus(profileId: string, userId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from("profile_likes")
      .select("id")
      .eq("profile_id", profileId)
      .eq("user_id", userId)
      .single();

    if (error && error.code !== "PGRST116") throw error;
    return !!data;
  },

  // Get related profiles
  async getRelatedProfiles(profileId: string, limit = 6): Promise<Profile[]> {
    const { data: profile } = await supabase
      .from("profiles")
      .select("profession, skills")
      .eq("id", profileId)
      .single();

    if (!profile) return [];

    let query = supabase
      .from("profiles")
      .select("*")
      .neq("id", profileId)
      .limit(limit);

    if (profile.profession) {
      query = query.ilike("profession", `%${profile.profession}%`);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  },
};
